import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
});

// Token management
export const setSessionToken = (token) => localStorage.setItem('session_token', token);
export const getSessionToken = () => localStorage.getItem('session_token');
export const clearSessionToken = () => localStorage.removeItem('session_token');

// Add Bearer token to all requests from localStorage
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('session_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth
export const authSession = (sessionId) => api.get(`/auth/session?session_id=${sessionId}`);
export const authMe = () => api.get('/auth/me');
export const authLogout = () => {
  localStorage.removeItem('session_token');
  return api.post('/auth/logout');
};

// Authenticated fetch helper — adds Bearer token automatically
export const authFetch = (url, options = {}) => {
  const token = getSessionToken();
  const headers = { ...options.headers };
  if (token) headers.Authorization = `Bearer ${token}`;
  return fetch(url, { ...options, headers });
};

// Parcels
export const getParcels = (params = {}) => api.get('/parcels', { params });
export const getParcel = (parcelId) => api.get(`/parcels/${parcelId}`);
export const analyzeParcel = (parcelId) => api.post(`/parcels/${parcelId}/analyze`);
export const getParcelAnalyses = (parcelId) => api.get(`/parcels/${parcelId}/analyses`);
export const createManualParcel = (data) => api.post('/parcels/manual', data);
export const deleteParcel = (parcelId) => api.delete(`/parcels/${parcelId}`);
export const getManualParcels = () => api.get('/parcels-manual');
export const updateParcel = (parcelId, data) => api.put(`/parcels/${parcelId}`, data);

// Follow parcels
export const followParcel = (parcelId) => api.post(`/parcels/${parcelId}/follow`);
export const unfollowParcel = (parcelId) => api.delete(`/parcels/${parcelId}/follow`);

// Modifications
export const createModification = (data) => api.post('/modifications', data);
export const getModifications = () => api.get('/modifications');

// Export
export const getParcelPdfUrl = (parcelId) => `${API_BASE}/export/parcel/${parcelId}/pdf`;

// Stats
export const getStats = (region) => api.get('/stats', { params: region ? { region } : {} });
export const getCommunes = (region) => api.get('/communes', { params: region ? { region } : {} });
// Cultures : référentiel géré dans l'écran admin « Cultures ». `saisie` écarte les libellés « à préciser ».
export const getCultures = (region, saisie = false) => api.get('/cultures', { params: { ...(region ? { region } : {}), ...(saisie ? { saisie: true } : {}) } });
export const getCultureReferentiel = (region) => api.get('/cultures/referentiel', { params: region ? { region } : {} });

// My Dashboard
export const getMyDashboard = () => api.get('/my/dashboard');

// ML Detection
export const runDetection = () => api.post('/detection/run', { force: true });
export const getDetectionJobs = () => api.get('/detection/jobs');

// Sentinel-2
export const getSentinel2Info = () => api.get('/sentinel2/info');

// Admin
export const getAdminUsers = () => api.get('/admin/users');

// Stripe
export const getStripePlan = () => api.get('/stripe/plan');
export const getSubscriptionStatus = () => api.get('/stripe/subscription-status');
export const createCheckoutSession = (originUrl, planId) => api.post('/stripe/create-checkout-session', { origin_url: originUrl, plan_id: planId });
export const getCheckoutStatus = (sessionId) => api.get(`/stripe/checkout-status/${sessionId}`);
export const getAdminUsersStripe = () => api.get('/stripe/admin/users');
export const toggleManualAccess = (userId, manualAccess) => api.post('/stripe/admin/toggle-access', { user_id: userId, manual_access: manualAccess });

// Admin Dashboard
export const getAdminOverview = () => api.get('/admin/stats/overview');
export const getAdminUsersPerDay = () => api.get('/admin/stats/users-per-day');
export const getAdminAllUsers = () => api.get('/admin/all-users');
export const toggleAdminRole = (userId, isAdmin) => api.post('/admin/toggle-admin', { user_id: userId, is_admin: isAdmin });
export const getStripeConfig = () => api.get('/admin/stripe-config');
export const saveStripeConfig = (data) => api.post('/admin/stripe-config', data);

// Phyto
export const getPhytoInterventions = (parcelId) => api.get(`/phyto/interventions`, { params: { parcel_id: parcelId } });
export const createPhytoIntervention = (data) => api.post('/phyto/interventions', data);
export const getPhytoSummary = (parcelId) => api.get(`/phyto/parcel/${parcelId}/summary`);
export const getPhytoPdfUrl = (parcelId) => `${API_BASE}/phyto/parcel/${parcelId}/pdf`;

export default api;
